// CONTRACT:C3-CAPABILITY-PLUGIN.1.0
/** THE DENSITY CAPABILITY — ambient clutter, the "haystack".
 *
 *  WHAT IT DOES: publishes ~153 REAL, mundane Hormuz movement tracks (the haystack from the
 *  TDFBot-Dataset-Intelligence corpus) onto the envelope bus so that, LATER, sealed/protected
 *  objects sit in a SEA OF REAL TRACKS instead of an empty map (the needle-in-a-haystack
 *  premise). It is the COP's ambient-density backdrop.
 *
 *  SEAM — PUBLISH-ONLY (mirrors chatCapability/multiIntCapability's seam): this capability only
 *  PUBLISHES its bundled dataset via ctx.data.publish. It does NOT paint the map and does NOT
 *  write the engine (CapabilityEngine has no write surface; this capability never touches it).
 *  The HOST records every published envelope, runs it through the PDP, and paints ONLY the
 *  cleared-visible slice for the current subject. The tracks are `kind: "track"`, so the host
 *  draws them on the EXISTING tracks layer (tracksCapability's lane) — this capability declares
 *  NO layer of its own, so the two never fight over the same kind.
 *
 *  HARD RULE — CLEAR-SHELL ONLY (the pretty-not-real trap, refused): the dataset is unclassified
 *  ambient density. Every envelope is `payload_mode: "clear"` + `leakage_profile: "clear"`, with
 *  NO `payload.encrypted_ref`, NO fabricated sealed `<__tdf>` inner, NO fake decrypt, NO claimed
 *  protection. By the PDP's isProtected() rule NONE of these is protected (no 🔒). Source markings
 *  are preserved verbatim (honest clear-shell ABAC labels) so the host's clearance projection can
 *  shape the density — a clear-shell marking is NOT a seal.
 *
 *  SDK PURITY (the dogfood discipline): imports ONLY the public C3 SDK (@tak-tdf/contracts) + its
 *  OWN bundled dataset module. NO host internal, NO abac-pdp, NO pdp.ts, NO engine write. */

import type {
  Capability,
  HostContext,
  Disposable,
} from "../../../../packages/contracts-ts/src/index.ts";
import { densityEnvelopesAt } from "./densityDataset.ts";

/** The capability id. */
export const DENSITY_CAP_ID = "scop:cap:density";

/** Re-publish cadence (ms). The haystack dead-reckons on the shared epoch clock; the COP
 *  interpolates between updates (deckBackend tween), so ~1Hz reads as smooth, continuous
 *  motion. Re-publishing the SAME ids is an engine UPDATE (the dot moves), never a dup. */
const DENSITY_TICK_MS = 1_000;

/** Build the ambient-density capability. NO host argument, NO PDP, NO engine write — the host's
 *  seam does the clearance filtering and the painting for us. */
export function createDensityCapability(): Capability {
  let disposables: Disposable[] = [];

  return {
    id: DENSITY_CAP_ID,

    async activate(ctx: HostContext): Promise<void> {
      // PUBLISH-ONLY + ALIVE: stream the bundled haystack onto the bus, then re-stream it each
      // tick at its dead-reckoned position so the ambient backdrop MOVES (it was publish-once-
      // and-freeze). Each clear-shell envelope flows out through ctx.data.publish; the HOST
      // records it, runs it through the PDP, and paints ONLY the cleared-visible slice (we never
      // subscribe, never paint, never write the engine). We declare no layer: the tracks are
      // `kind: "track"`, painted on the existing tracks layer. Re-publishing a stable id MOVES
      // the dot (engine update) — the host coalesces all per-tick upserts into one frame write.
      const emitAll = (): void => {
        for (const env of densityEnvelopesAt(Date.now())) {
          // fire-and-forget: publish is synchronous on the in-process bus; a stray reject must
          // never kill the interval (the next tick re-publishes the whole set anyway).
          void ctx.data.publish(env);
        }
      };
      emitAll(); // paint immediately at the current position
      const timer = setInterval(emitAll, DENSITY_TICK_MS);
      disposables.push({ dispose: () => clearInterval(timer) });
    },

    deactivate(): void {
      for (const d of disposables.splice(0)) {
        try {
          d.dispose();
        } catch {
          // teardown is best-effort
        }
      }
    },
  };
}
